import { IonItem, IonList, IonRange } from "@ionic/react";
import { CategoryScale, ChartData, ChartDataset, ChartOptions, Tooltip, TooltipItem } from "chart.js";
import { Chart as ChartJS }                                                           from "chart.js/auto";
import { BarWithErrorBar, BarWithErrorBarsController } from "chartjs-chart-error-bars";
import zoomPlugin from "chartjs-plugin-zoom";
import { FormatNumberSI }                 from "functions/Formatting";
import { Glob, useLoc }                   from "functions/Language";
import { HarmResponse, SupportedDevices } from "functions/networking/NetTypes";
import { FC, forwardRef, useEffect, useMemo, useReducer, useRef } from "react";
import { Bar, Chart }                from "react-chartjs-2";
import { ChartJSOrUndefined, ChartProps, TypedChartComponent } from "react-chartjs-2/dist/types";


ChartJS.register(CategoryScale, zoomPlugin, BarWithErrorBar, BarWithErrorBarsController);
// @ts-ignore
(Tooltip.positioners).bottom = function(items, epos) {
  // @ts-ignore
  const pos = Tooltip.positioners.average(items, epos);

  // Happens when nothing is found
  if (pos === false) {
    return false;
  }

  // @ts-ignore
  const chart = this.chart;

  return {
    x: pos.x,
    y: chart.chartArea.bottom,
    yAlign: 'bottom',
  };
};


const MinMaxChart = forwardRef<ChartJSOrUndefined<typeof BarWithErrorBarsController.id>, Omit<ChartProps<typeof BarWithErrorBarsController.id>, 'type'>>(
  function MMChart(props, ref) { return <Chart {...props} ref={ref} type={BarWithErrorBarsController.id} /> }
) as TypedChartComponent<typeof BarWithErrorBarsController.id>;

type MinMax = { min: HarmResponse["values"], max: HarmResponse["values"] };
const colors = ["#ff0000", "#ff0000", "#00ee00", "#3f3fff"];
const maxColors = ["#00cfff", "#00cfff", "#ff11ff", "#c0c000"];

function DataSet(data: (number|null)[][], _: unknown, l: number, phaseNames: string[]) : ChartDataset<"bar"> {
  return {
    label: phaseNames[l],
    backgroundColor: colors[l],
    data: data.map(v => v[l])
  }
}

function MMDataSet(data: (number|null)[][], mm: MinMax, l: number, phaseNames: string[]) : ChartDataset<"barWithErrorBars"> {
  return {
    label: phaseNames[l],
    backgroundColor: colors[l],
    data: mm.max.map((_, j) => ({
      y: data[j][l] ?? NaN,
      yMin: mm.max[j][l] ?? NaN,
      yMax: mm.max[j][l] ?? NaN
    })),
    errorBarWhiskerRatio: 1,
    errorBarWhiskerColor: maxColors[l]
  }
}

export const HarmonicsChart : FC<{
  device: SupportedDevices,
  data: HarmResponse["values"],
  phaseNames: string[],
  units: string,
  minMax?: MinMax,
  forceRedraw?: unknown,
  labels?: string[]
  skipFirst?: boolean,
	singlePhase?: boolean,
}> = (props) => {
  const loc = useLoc(glob);

  const chart = useRef<ChartJSOrUndefined<"bar"> & ChartJSOrUndefined<"barWithErrorBars">>()
	const data = useMemo<ChartData<"bar"> | ChartData<"barWithErrorBars">>(() => {
		const labels = props.labels ?? props.data.map((_, i) => i+1);

		const data_fn = props.minMax ? MMDataSet : DataSet;

		if (props.singlePhase) {
			return {
				datasets: [data_fn(props.data, props.minMax!, 0, ["", ""])],
				labels: labels,
			} as any;
		}
		else {
			return {
				datasets: [1, 2, 3].map(i => data_fn(props.data, props.minMax!, i, props.phaseNames)),
				labels: labels,
			} as any;
		}
	}, [props.data, props.minMax, props.forceRedraw, props.labels, props.phaseNames, props.device]);

	const upperBound = props.data.length;

  const range = useRef({lower: props.skipFirst ? 2 : 1, upper: upperBound });
  const preview = useRef({ lower: props.skipFirst ? 2 : 1, upper: upperBound });

  const [,redraw] = useReducer(v => v + 1, 0);
  useEffect(() => {
    range.current = { lower: props.skipFirst ? 2 : 1, upper: upperBound };
    preview.current = range.current;
    redraw();
  }, [props.skipFirst, upperBound]);

  const options = useRef<ChartOptions<"bar"> & ChartOptions<"barWithErrorBars">>({
    maintainAspectRatio: false,
    animation: false,
    scales: {
      x: {
        ticks: {
          autoSkip: false,
          callback (i) {
            let labels = this.getLabels();
            if (typeof i === "string")
              return i;
            return i % 5 === 4 ? labels[i] : undefined
          }
        },
        grid: {
          offset: false,
        }
      },
      y: {
        ticks: {
          callback(v) {
            if (typeof v === "string") return v;
            else return FormatNumberSI(v) + props.units
          }
        }
      }
    },
    plugins: {
      legend: {
        display: !props.singlePhase,
      },
      tooltip: {
        intersect: false,
        axis: "x",
        position: "bottom" as any,
        mode: "index",
        callbacks: {
          label(ctx : TooltipItem<"bar"> & TooltipItem<"barWithErrorBars">) {
            let label = ctx.dataset.label;
            if (label)
              label += ": "
            if (ctx.parsed.y !== null) {
              label += FormatNumberSI(ctx.parsed.y);
            }
            if (ctx.parsed.yMin !== undefined && ctx.parsed.yMax !== undefined) {
              label += `(max: ${FormatNumberSI(ctx.parsed.yMax as number, true)})`;
            }
            return label;
          }
        }
      },
      zoom: {
        zoom: {
          drag: {
            enabled: true,
            backgroundColor: "rgba(250, 100, 0, 0.3)",
          },
          mode: "x",
          onZoomComplete: (ctx) => {
            let ax = ctx.chart.scales["x"];
            if (range.current.lower !== ax.min || range.current.upper !== ax.max) {
              range.current = { lower: ax.min, upper: ax.max };
              preview.current = range.current;
              redraw();
              setTimeout(() => {
                chart.current?.zoomScale("x", { min: range.current.lower, max: range.current.upper });
              }, 0);
            }
          }
        }
      }
    }
  });

  useEffect(() => {
    options.current.scales!.y!.ticks!.callback = (v) => {
      if (typeof v === "string") return v;
      else return FormatNumberSI(v) + props.units
    };
		(options.current.plugins!.legend as any).display = !props.singlePhase;
		chart.current!.options = options.current;
		chart.current?.update();
  }, [props.units, props.device, props.singlePhase]);

  useEffect(() => {
    chart.current?.zoomScale("x", {min: range.current.lower, max: range.current.upper });
  }, [props.minMax, props.skipFirst, upperBound]);

  const previewPlugin = useRef({
    id: "zoomPreview",
    beforeDraw(chart : ChartJS) {
      if (preview.current.lower < range.current.lower
        || preview.current.upper > range.current.upper)
        return;

      let yAxis = chart.scales.y;
      let xAxis = chart.scales.x;

      let s = xAxis.width / (range.current.upper - range.current.lower + 1);

      let x1 = xAxis.left + (preview.current.lower - range.current.lower) * s;
      let x2 = xAxis.left + (preview.current.upper - range.current.lower + 1) * s;
      let ctx = chart.ctx;

      ctx.save();

      ctx.fillStyle = "#cccccc";
      ctx.fillRect(xAxis.left, yAxis.top, x1 - xAxis.left, yAxis.height);
      ctx.fillRect(x2, yAxis.top, xAxis.right - x2, yAxis.height);

      ctx.restore();
    }
  });

  return <IonList style={{ height: "100%", display: "flex", flexDirection: "column" }}>
    <IonItem lines="none" style={{ flex: "auto", "--min-height": "100%" }}>
      <div style={{ width: "100%", marginInline: "auto", height: "100%" }}>
        {props.minMax === undefined
          ? <Bar
            style={{ position: "absolute", top: "0" }}
            ref={chart as any}
            data={data as any}
            onDoubleClick={() => {
              chart.current?.resetZoom();
              range.current = { lower: props.skipFirst ? 2 : 1, upper: upperBound };
              preview.current = range.current;
              redraw();
            }}
            options={options.current}
            plugins={[previewPlugin.current]}
          />
          : <MinMaxChart
            style={{ position: "absolute", top: "0" }}
            ref={chart}
            data={data as any}
            onDoubleClick={() => {
              chart.current?.resetZoom();
              range.current = { lower: props.skipFirst ? 2 : 1, upper: upperBound };
              preview.current = range.current;
              redraw();
            }}
            options={options.current}
            plugins={[previewPlugin.current]}
          />
        }</div>
    </IonItem>

    <IonItem>
      <IonRange
        label={loc.range}
        style={{
          paddingLeft: 15,
          paddingRight: 15,
        }}
        dualKnobs
        min={props.skipFirst ? 2 : 1}
        max={props.data.length}
        value={preview.current}

        onIonChange={e => {
          let val = e.detail.value as { lower: number, upper: number };
          chart.current?.zoomScale("x", {min: val.lower, max: val.upper });
          range.current = val;
          preview.current = val;
          chart.current?.update();
        }}

        onIonInput={e => {
          let val = e.detail.value as { lower: number, upper: number };
          preview.current = val;
          chart.current?.update();
        }}
      />
    </IonItem>
  </IonList>
}


const glob = Glob({
	en: {
		range: "Plot range"
	},
	cs: {
		range: "Rozsah grafu"
	},
	pg: {
		range: "Faixa do gráfico"
	},
	es: {
		range: "Gama de gráficos"
	}
} as const);
